import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import ImgHubspot from '../3/hubspot.png'
import ImgEcom from '../3/Ecom.png'
import ImgSupport from '../3/customer support agent.png'
import ImgAds from '../3/ad agency.png'

type Workflow = {
    id: number
    title: string
    stack: string
    image: string
    bullets: string[]
    goal: string
    details: string[]
}

const SocialAutomationPage: React.FC = () => {
    const [active, setActive] = useState<Workflow | null>(null)
    const [expanded, setExpanded] = useState(false)

    // Lock scroll and add Escape to close modal
    useEffect(() => {
        if (!active) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setActive(null)
        }
        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onKey)
        return () => {
            document.body.style.overflow = ''
            window.removeEventListener('keydown', onKey)
        }
    }, [active])

    // Reset expanded when opening a new workflow
    useEffect(() => {
        if (active) setExpanded(false)
    }, [active])
    const workflows: Workflow[] = [
        {
            id: 1,
            title: 'Workflow 1 — Support Agent + Ticketing',
            stack: 'Circle + n8n + Airtable + AI',
            image: ImgSupport,
            bullets: [
                'Automatically answers technical questions in Circle (AI + confidence score).',
                'Creates and updates tickets in Airtable with statuses (Not replied, Replied, Solved, Urgent).',
                'Parses comments and marks [Solved] with a closing message.',
                'Single webhook entry with action-based routing (create, check_status, solve).',
                'Extensible to Slack/Discord/Email with the same orchestrator.'
            ],
            goal: 'Auto‑reply to community questions while maintaining a clean ticketing backlog.',
            details: [
                'Ingest new Circle posts via webhook, create an Airtable ticket, and draft an HTML answer with a confidence score.',
                'Periodic status checks pull post comments, normalize content, and re‑classify: Not replied, Replied, Solved, Urgent.',
                'When solved, add a final comment and update the post title with [Solved].',
                'Single n8n endpoint with an action switch keeps the integration simple and robust.'
            ]
        },
        {
            id: 2,
            title: 'Workflow 2 — Automated Meta Ads Agency',
            stack: 'n8n + Airtable + Gemini + Perplexity + Meta Graph API',
            image: ImgAds,
            bullets: [
                'Automated product research & ICP (deep dive via Perplexity).',
                'Generate angles and VSL scripts (Gemini) + human shortlist.',
                'Performance tracking (ROAS, CPC, conversions) via the Meta Graph API.',
                '“Best ads” flywheel: scrape video link, transcript, and feed future scripts.',
                'Robust Airtable schemas: Products, Angles, Scripts, Performance.'
            ],
            goal: 'Industrialize creative ideation, production, and iteration across multiple brands.',
            details: [
                'From a product brief, generate ICP, positioning, objections, and tone with a Perplexity deep dive.',
                'Create multiple angles and full VSL scripts using Gemini; shortlist with human feedback and revisions.',
                'Pull live performance via Meta Graph API; normalize metrics and maintain a performance history.',
                'Mark “best ads”, auto‑extract the true video URL, transcribe it, and use the learnings to improve future scripts.'
            ]
        },
        {
            id: 3,
            title: 'Workflow 3 — E‑commerce Price Monitoring',
            stack: 'n8n + LLM + HTTP + Airtable/Sheets/DB',
            image: ImgEcom,
            bullets: [
                'Multi‑source scraping (site, Google Shopping, marketplaces) with LLM fallback.',
                'Strict price normalization (schema, currency, rounding, validation).',
                'Alert rules on deltas/stock with noise control (throttle/dedup).',
                'Master DB upsert + rich alerts (old/new price, %).',
                'Daily BI exports and long‑term history.'
            ],
            goal: 'Continuously track competitor/product prices and react to meaningful deltas.',
            details: [
                'Ingest prices from direct site pages, Google Shopping results, and marketplace APIs in parallel.',
                'Normalize each record to a strict schema with currency conversion, rounding, and numeric validation.',
                'Compare with the last known price and emit alerts for low price, significant deltas, or stock changes.',
                'Upsert a master table while keeping a long‑term log for BI dashboards.'
            ]
        },
        {
            id: 4,
            title: 'Workflow 4 — Sales Call Analysis & HubSpot Update',
            stack: 'Google Drive/Zoom + n8n + OpenAI + HubSpot',
            image: ImgHubspot,
            bullets: [
                'Ingest transcript (Drive/Zoom/Gmail) → extract name/email (OpenAI).',
                '“Sales Analyst” AI agent with structured JSON output (persona, red_flags, opportunities, score).',
                'Merge branches (contact + analysis) and upsert HubSpot Contact + Deal.',
                'Best practices: idempotency, property mapping, retries and logs.',
                'Variants: Slack alerts, summary email, automatic task creation.'
            ],
            goal: 'Turn raw discovery call transcripts into structured CRM insights and next steps.',
            details: [
                'Fetch a transcript from Drive/Zoom or email; convert PDF/doc to clean text when needed.',
                'Extract contact basics with a lightweight model; run a multi‑phase analysis agent with a strict JSON schema.',
                'Combine contact and analysis branches, then upsert HubSpot Contact and Deal with mapped properties.',
                'Add alerts and email summaries for rapid sales follow‑up.'
            ]
        }
    ]

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
            {/* Bouton retour */}
            <Link
                to="/"
                className="fixed top-6 left-6 z-50 bg-white text-blue-600 border border-blue-200 shadow-md rounded-full px-5 py-2 font-bold hover:bg-blue-50 transition"
            >
                ← Back to Home
            </Link>

            {/* Hero Section */}
            <section className="py-20 px-4 max-w-6xl mx-auto text-center">
                <h1 className="text-5xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 mb-6">
                    AI Workflows & Automation — Real Cases (Samples)
                </h1>
                <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                    These 4 are just sample workflows with real screenshots and clear impact. Your setup will be tailored to your stack and goals.
                </p>
            </section>

            {/* Workflows Grid */}
            <section className="py-12 px-4 max-w-7xl mx-auto">
                <div className="grid gap-10 md:grid-cols-2">
                    {workflows.map((wf, index) => (
                        <motion.div
                            key={wf.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.6, delay: index * 0.05 }}
                            className="group rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur hover:shadow-2xl hover:scale-[1.02] transition-all cursor-pointer"
                            onClick={() => setActive(wf)}
                        >
                            <div>
                                <div className="px-5 pt-5 pb-3 flex items-center justify-between">
                                    <div>
                                        <h3 className="text-2xl font-bold text-slate-800 dark:text-white">{wf.title}</h3>
                                        <p className="text-sm text-slate-600 dark:text-gray-300">{wf.stack}</p>
                                    </div>
                                    <button className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-blue-700 text-white text-sm font-medium shadow hover:bg-blue-800 transition-all">
                                        View details
                                        <span>→</span>
                                    </button>
                                </div>
                                <div className="px-5">
                                    <div className="bg-white p-2 md:p-3 rounded-xl">
                                        <img
                                            src={wf.image}
                                            alt={wf.title}
                                            className="w-full h-auto object-contain aspect-[16/9] rounded-xl shadow-md group-hover:shadow-xl group-hover:brightness-105 group-hover:scale-[1.01] transition-all"
                                            loading="lazy"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <ul className="space-y-2 text-gray-600 dark:text-gray-200">
                                    {wf.bullets.map((b, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <span className="mt-1 text-blue-600 dark:text-blue-400">•</span>
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 px-4 max-w-4xl mx-auto text-center">
                <h2 className="text-3xl font-bold mb-4">Want these workflows in your stack?</h2>
                <p className="text-gray-600 dark:text-gray-300 mb-8">Fast audit, clear estimate, deployment in days — not months.</p>
                <a href="https://www.upwork.com/freelancers/~01902b7448e204d256?mp_source=share" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-3 bg-blue-700 dark:bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-800 dark:hover:bg-blue-700 transition-all">Discuss your case on Upwork</a>
            </section>

            {/* Details Modal */}
            {active && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center"
                >
                    <div
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={() => setActive(null)}
                    />
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 140, damping: 18 }}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Workflow details"
                        className="relative z-10 w-[95vw] max-w-4xl mx-auto rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl max-h-[90vh] flex flex-col"
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 sticky top-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur z-10">
                        <div>
                                <h3 className="text-2xl font-bold text-slate-800 dark:text-white">{active.title}</h3>
                                <p className="text-sm text-slate-600 dark:text-gray-300">{active.stack}</p>
                        </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setExpanded((v) => !v)}
                                    className="px-3 py-1 rounded-full text-sm border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                                >
                                    {expanded ? 'Fit to screen' : 'Full size'}
                                </button>
                                <a
                                    href={active.image}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="px-3 py-1 rounded-full text-sm border border-gray-300 dark:border-gray-700 text-blue-700 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                                >
                                    Open in new tab
                                </a>
                                <button
                                    onClick={() => setActive(null)}
                                    className="rounded-full px-3 py-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                    aria-label="Close"
                                >
                                    ✕
                                </button>
                        </div>
                        </div>
                        <div className="p-6 space-y-6 overflow-y-auto max-h-[80vh]">
                            <div className="bg-white dark:bg-gray-800 p-2 md:p-3 rounded-xl">
                                <img
                                    src={active.image}
                                    alt={active.title}
                                    className={expanded
                                        ? 'w-full h-auto rounded-lg shadow'
                                        : 'w-full h-auto object-contain aspect-[16/9] rounded-lg shadow'}
                                />
                            </div>
                            <div>
                                <h4 className="text-xl font-semibold mb-2 text-slate-800 dark:text-white">Goal</h4>
                                <p className="text-gray-600 dark:text-gray-300">{active.goal}</p>
                            </div>
                            <div>
                                <h4 className="text-xl font-semibold mb-2 text-slate-800 dark:text-white">How it works</h4>
                                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                                    {active.details.map((d, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <span className="mt-1 text-blue-600 dark:text-blue-400">→</span>
                                            <span>{d}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                                <a href="https://www.upwork.com/freelancers/~01902b7448e204d256?mp_source=share" target="_blank" rel="noopener noreferrer" className="px-6 py-2 rounded-lg bg-blue-700 dark:bg-blue-600 text-white font-medium hover:bg-blue-800 dark:hover:bg-blue-700 transition">Implement this workflow on Upwork</a>
                                <button onClick={() => setActive(null)} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition">Close</button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}


            {/* Final CTA Section */}
            
        </div>
    )
}

export default SocialAutomationPage